import { usePermissionsStore } from "../permissions/permissions_store";
import { useEffect, useState } from "react";
import CRMService from "../../services/crm.service";

const FIELDS = [
  { key: "mailing_address", label: "Mailing address" },
  { key: "physical_address", label: "Physical address" },
];

export default function ContactAddresses({ identityId }) {
  const { canView, canEdit } = usePermissionsStore();
  const hasAccess = canView("crm.custom_fields");
  const readOnly = !canEdit("crm.custom_fields");
  const [definitions, setDefinitions] = useState([]);
  const [values, setValues] = useState({});
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true); setEditing(false); setError(""); setValues({});
    if (!hasAccess || !identityId) { setLoading(false); return; }
    Promise.all([CRMService.getCustomFieldDefinitions("identity"), CRMService.getCustomFieldValues("identity", identityId)])
      .then(([defs, vals]) => {
        if (!active) return;
        setDefinitions(defs.data || []);
        const next = {};
        for (const field of FIELDS) {
          const definition = (defs.data || []).find((d) => d.field_key === field.key);
          next[field.key] = (vals.data || []).find((v) => String(v.field_definition_id) === String(definition?.id))?.value_text || "";
        }
        setValues(next);
      }).catch(() => { if (active) setError("Unable to load addresses."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [identityId, hasAccess]);

  const save = async () => {
    if (readOnly) return;
    setSaving(true); setError("");
    try {
      const defs = [...definitions];
      for (const field of FIELDS) {
        if ((draft[field.key] || "") === (values[field.key] || "")) continue;
        let definition = defs.find((d) => d.field_key === field.key);
        if (!definition) {
          try {
            definition = (await CRMService.createCustomFieldDefinition({ entity_type: "identity", field_key: field.key, field_label: field.label, field_type: "textarea", sort_order: 10 })).data;
          } catch (err) {
            if (err.response?.status !== 409) throw err;
            definition = (await CRMService.getCustomFieldDefinitions("identity")).data.find((d) => d.field_key === field.key);
          }
          if (!definition) throw new Error("Address field unavailable");
          defs.push(definition);
        }
        await CRMService.upsertCustomFieldValue({ entity_type: "identity", entity_id: identityId, field_definition_id: definition.id, value: draft[field.key] || "" });
      }
      setDefinitions(defs); setValues(draft); setEditing(false);
    } catch (_) { setError("Unable to save all addresses. Please try again."); }
    finally { setSaving(false); }
  };

  if (!hasAccess || !identityId) return null;
  return <section className="crm-section" aria-label="Contact addresses">
    <div className="crm-section-title">Addresses</div>
    {error && <p role="alert">{error}</p>}
    {loading ? <p>Loading addresses…</p> : <>
      {FIELDS.map((field) => <div key={field.key} className="crm-info-row">
        <label className="crm-info-label" htmlFor={`crm-${field.key}`}>{field.label}</label>
        {editing ? <textarea id={`crm-${field.key}`} className="uk-textarea" disabled={saving} value={draft[field.key] || ""}
          onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })} />
          : <span className="crm-info-value" style={{ whiteSpace: "pre-line" }}>{values[field.key] || "Not provided"}</span>}
      </div>)}
      {editing ? <>
        <button type="button" className="uk-button uk-button-default uk-button-small" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save addresses"}</button>
        <button type="button" className="uk-button uk-button-text" disabled={saving} onClick={() => setEditing(false)}>Cancel</button>
      </> : !readOnly && <button type="button" className="uk-button uk-button-text" disabled={Boolean(error)} onClick={() => { setDraft(values); setEditing(true); }}>Edit addresses</button>}
    </>}
  </section>;
}
